// Shared support-ticket contract (typed help/intake framework, commit 2).
//
// Sibling of feedbackIntakes.js. That module owns the intake AGGREGATE
// vocabulary (types, statuses, transitions); this one owns the support-ticket
// VERTICAL: the GSC field set, the versioned technical-context envelope, the
// delivery/dispatch vocabularies, and the field-provenance labels.
//
// Consumed by:
//   - apps/api/src/services/supportContext.service.js  (allowlist enforcement)
//   - apps/api/src/services/gscTicket.service.js       (renderer field order)
//   - apps/api/src/services/supportTicketDelivery.service.js (mode/status graph)
//   - migration 026's CHECK constraints and column widths
//   - apps/web/src/features/feedbackIntake/*            (form + status labels)
//
// Pure data plus pure functions: no React, no Node-only imports, no API access.
// Deliberately NOT here: the sensitive-content detector patterns. Those are
// server-authoritative and stay in apps/api/src/services/sensitiveContent.service.js
// so the full detection matrix is never shipped to a browser bundle where it
// could be read as an evasion guide. The web mirrors only an obvious subset.
//
// Values are matched EXACTLY as persisted — no trimming, no case folding — so a
// value outside a declared vocabulary is an unknown state a caller must refuse
// rather than repair.

// -------------------------------------------------------------------------
// Technical-context envelope
// -------------------------------------------------------------------------

// The schema version is stamped onto every persisted envelope. A reader that
// does not recognize the version must refuse to interpret the payload rather
// than guess which keys it holds.
export const SUPPORT_CONTEXT_SCHEMA_VERSION = 'support-context/1.0';

// The ONLY keys a client may propose. Everything else is dropped server side
// (its NAME is recorded in audit metadata; its value never is). The list is
// deliberately small and boring: nothing here can carry a cookie, a token, a
// hostname, DOM text, an input value, a query string, or a header.
export const SUPPORT_CONTEXT_ALLOWED_KEYS = Object.freeze([
  'capturedAt',
  'appVersion',
  'buildCommit',
  'environment',
  'routeTemplate',
  'pageTitle',
  'entityType',
  'roleIds',
  'authMode',
  'browserFamily',
  'browserVersion',
  'osFamily',
  'viewportWidth',
  'viewportHeight',
  'timezone',
  'online',
  'recentApiError',
  'featureFlags',
]);

// Fields the SERVER owns unconditionally. A client may send them; the server
// overwrites them from the resolved session and its own build configuration, so
// a forged claim changes nothing and is not worth rejecting the whole envelope
// over. (Unknown keys are a different case — they are dropped and reported.)
export const SUPPORT_CONTEXT_TRUSTED_KEYS = Object.freeze([
  'appVersion',
  'buildCommit',
  'environment',
  'roleIds',
  'authMode',
]);

// A sanitized recent API failure helps triage "it broke when I clicked Export"
// without shipping a request body. Exactly these four fields, nothing else:
// no URL (which can carry ids and query values), no response body, no headers.
export const SUPPORT_CONTEXT_RECENT_API_ERROR_KEYS = Object.freeze([
  'method',
  'routeTemplate',
  'statusCode',
  'requestId',
]);

// Feature-flag NAMES only, from a fixed allowlist. An arbitrary flag map is an
// open channel for client-chosen strings, so the envelope carries names the
// server already knows about and drops the rest.
export const SUPPORT_CONTEXT_ALLOWED_FEATURE_FLAGS = Object.freeze([
  'reviewTools',
  'feedbackDictation',
  'personaShortcuts',
  'siteAccessConsole',
]);

// Bounds are generous for prose-ish values and tight for enumerable ones. Every
// string value in the envelope is bounded so no single field can inflate the
// intake payload.
export const SUPPORT_CONTEXT_STRING_MAX_LENGTH = 200;
export const SUPPORT_CONTEXT_MAX_FEATURE_FLAGS = 24;
export const SUPPORT_CONTEXT_VIEWPORT_MIN = 0;
export const SUPPORT_CONTEXT_VIEWPORT_MAX = 20000;
export const SUPPORT_CONTEXT_MAX_ROLE_IDS = 24;

export function isSupportContextKey(value) {
  return SUPPORT_CONTEXT_ALLOWED_KEYS.includes(value);
}

export function isSupportContextTrustedKey(value) {
  return SUPPORT_CONTEXT_TRUSTED_KEYS.includes(value);
}

// -------------------------------------------------------------------------
// GSC support-ticket fields
// -------------------------------------------------------------------------

// Field order is the ORDER THE RENDERED EMAIL USES. The service desk reads these
// tickets by eye against a printed form, so the order is part of the contract,
// not a presentation detail.
export const SUPPORT_TICKET_FIELDS = Object.freeze([
  'name',
  'phone',
  'email',
  'site',
  'organization',
  'department',
  'buildingNumber',
  'roomNumber',
  'fullComputerName',
  'category',
  'description',
]);

// Bounds are measured in UTF-16 code units (String.length) — the same unit
// NVARCHAR counts, and identical in the current Node and browser runtimes.
// Exactly the limit is accepted; one more is not.
export const SUPPORT_TICKET_FIELD_LIMITS = Object.freeze({
  name: 200,
  phone: 64,
  email: 254,
  site: 200,
  organization: 200,
  department: 200,
  buildingNumber: 64,
  roomNumber: 64,
  fullComputerName: 128,
  category: 96,
  // Same 4000-character budget as FEEDBACK_INTAKE_DETAIL_MAX_LENGTH: the
  // description IS a typed intake detail field (`details.description`), and a
  // reporter must not have to learn two "how much can I type" rules.
  description: 4000,
});

// Persisted on the intake as `supportTicket.*`, EXCEPT `description`, which is
// stored as `details.description` so the shared readiness validator and the
// governed-record materializer keep reading one description for every intake
// type. This list is what a client may send inside `supportTicket`.
export const SUPPORT_TICKET_PAYLOAD_FIELDS = Object.freeze([
  ...SUPPORT_TICKET_FIELDS.filter((field) => field !== 'description'),
  'protectedInformationRequired',
  'cuiAcknowledged',
  'fieldProvenance',
  'attachments',
]);

// Readiness gate. Deliberately short: a service-desk ticket that cannot be
// routed or called back is worse than no ticket. Department, building, room, and
// computer name are genuinely optional — many PRIME users work remotely and a
// browser cannot discover any of them.
export const SUPPORT_TICKET_REQUIRED_FIELDS = Object.freeze([
  'name',
  'phone',
  'email',
  'site',
  'organization',
  'category',
  'description',
]);

// Human labels, used by BOTH the rendered email and the form, so a reporter and
// the service desk read the same words for the same field.
export const SUPPORT_TICKET_FIELD_LABELS = Object.freeze({
  name: 'Name',
  phone: 'Phone Number',
  email: 'E-mail Address',
  site: 'Site',
  organization: 'Organization',
  department: 'Department',
  buildingNumber: 'Building Number',
  roomNumber: 'Room Number',
  fullComputerName: 'Full Computer Name',
  category: 'Issue/request category',
  description: 'Detailed description',
});

// Categories are ids on the wire and labels in the UI/email. Free text is
// refused: the subject line is built from the label, and an unbounded
// client-chosen subject is both a routing problem and a header-injection risk.
export const SUPPORT_TICKET_CATEGORIES = Object.freeze([
  Object.freeze({ id: 'account_access', label: 'Account or access problem' }),
  Object.freeze({ id: 'sign_in', label: 'Sign-in or CAC authentication' }),
  Object.freeze({ id: 'data_accuracy', label: 'Incorrect or missing data' }),
  Object.freeze({ id: 'error_message', label: 'Error message or failed action' }),
  Object.freeze({ id: 'performance', label: 'Slow performance or timeout' }),
  Object.freeze({ id: 'reporting_export', label: 'Reporting or export problem' }),
  Object.freeze({ id: 'data_feed', label: 'Connector or data feed problem' }),
  Object.freeze({ id: 'other', label: 'Something else' }),
]);

export const SUPPORT_TICKET_MAX_ATTACHMENTS = 5;

export function isSupportTicketCategory(value) {
  return SUPPORT_TICKET_CATEGORIES.some((category) => category.id === value);
}

export function supportTicketCategoryLabel(value) {
  return SUPPORT_TICKET_CATEGORIES.find((category) => category.id === value)?.label || null;
}

// -------------------------------------------------------------------------
// Field provenance
// -------------------------------------------------------------------------

// Where a prefilled value came from. Shown next to every prefilled control so a
// reporter can tell a directory value they should correct in their profile from
// something they typed here, and so nothing pretends to be device-detected when
// a browser cannot detect it.
export const SUPPORT_TICKET_FIELD_PROVENANCE = Object.freeze([
  'directory_supplied',
  'user_entered',
  'device_supplied',
]);

export const SUPPORT_TICKET_FIELD_PROVENANCE_LABELS = Object.freeze({
  directory_supplied: 'From your PRIME profile',
  user_entered: 'Entered by you',
  device_supplied: 'Detected by your browser',
});

export function isSupportTicketFieldProvenance(value) {
  return SUPPORT_TICKET_FIELD_PROVENANCE.includes(value);
}

// -------------------------------------------------------------------------
// Delivery and dispatch
// -------------------------------------------------------------------------

// `draft_only` is the DEFAULT everywhere. It prepares and stores the exact
// artifact the reporter previewed and never contacts an external system, so no
// environment can start emailing the service desk by accident.
export const SUPPORT_DELIVERY_MODES = Object.freeze(['disabled', 'draft_only', 'email']);
export const SUPPORT_DELIVERY_MODE_DEFAULT = 'draft_only';

export const SUPPORT_DRAFT_ADDRESSING_MODES = Object.freeze([
  'server_configured',
  'user_addressed',
]);
export const SUPPORT_DRAFT_ADDRESSING_MODE_DEFAULT = 'server_configured';

export const SUPPORT_DISPATCH_STATUSES = Object.freeze([
  'draft',
  'queued',
  'sending',
  'accepted',
  'failed_retryable',
  'failed_permanent',
  'reconciliation_required',
  'cancelled',
]);

// Explicit edges. A dispatch never returns to `draft`: the draft is the artifact
// as previewed, and re-preparing produces a new rendered artifact version rather
// than rewinding a delivery attempt.
//
// `reconciliation_required` is the honest answer to "a provider call may have
// succeeded and PRIME cannot prove either way". SQL leasing gives database
// ownership of a row, NOT exactly-once external delivery: a worker can hand the
// message to the provider and die before recording acceptance. Resending in that
// state risks a duplicate email to the service desk, so the row stops here and
// waits for a human to confirm. It is TERMINAL on purpose — automatic recovery is
// exactly the behavior that would send the second copy.
export const SUPPORT_DISPATCH_STATUS_GRAPH = Object.freeze({
  draft: Object.freeze(['queued', 'cancelled']),
  queued: Object.freeze(['sending', 'cancelled']),
  sending: Object.freeze([
    'accepted',
    'failed_retryable',
    'failed_permanent',
    'reconciliation_required',
  ]),
  failed_retryable: Object.freeze([
    'sending',
    'failed_permanent',
    'reconciliation_required',
    'cancelled',
  ]),
  accepted: Object.freeze([]),
  failed_permanent: Object.freeze([]),
  reconciliation_required: Object.freeze([]),
  cancelled: Object.freeze([]),
});

export const SUPPORT_DISPATCH_TERMINAL_STATUSES = Object.freeze(
  SUPPORT_DISPATCH_STATUSES.filter((status) => SUPPORT_DISPATCH_STATUS_GRAPH[status].length === 0),
);

// Truthful UI strings. There is no "Sent": the only state that may claim an
// external system took the message is `accepted`, and dispatchStatusLabel()
// refuses even that without a provider delivery id.
export const SUPPORT_DISPATCH_STATUS_LABELS = Object.freeze({
  draft: 'Draft prepared (not sent)',
  queued: 'Queued for delivery',
  sending: 'Delivery in progress',
  accepted: 'Accepted by provider',
  failed_retryable: 'Delivery failed — PRIME will retry',
  failed_permanent: 'Delivery failed — not retrying',
  reconciliation_required: 'Delivery status needs review',
  cancelled: 'Delivery cancelled',
});

// Plain-language explanation for each state, so the reporter is never left to
// infer what "queued" means for their ticket.
export const SUPPORT_DISPATCH_STATUS_DETAILS = Object.freeze({
  draft:
    'PRIME saved your ticket and the exact email text. Nothing has been emailed to the service desk.',
  queued: 'PRIME will hand this ticket to the email provider. It has not been accepted yet.',
  sending: 'PRIME is handing this ticket to the email provider right now.',
  accepted: 'The email provider accepted this ticket and returned a delivery id.',
  failed_retryable: 'The email provider could not take this ticket yet. PRIME will try again.',
  failed_permanent:
    'The email provider refused this ticket and PRIME will not retry. Contact the service desk another way.',
  reconciliation_required:
    'PRIME handed this ticket to the email provider but could not confirm the outcome. It may or may not have arrived. PRIME will NOT try again on its own, because that could send the service desk a second copy — someone will confirm what happened.',
  cancelled: 'Delivery was cancelled. Nothing was emailed to the service desk.',
});

export function isSupportDeliveryMode(value) {
  return SUPPORT_DELIVERY_MODES.includes(value);
}

export function isSupportDraftAddressingMode(value) {
  return SUPPORT_DRAFT_ADDRESSING_MODES.includes(value);
}

export function isSupportDispatchStatus(value) {
  return SUPPORT_DISPATCH_STATUSES.includes(value);
}

export function canTransitionSupportDispatchStatus(from, to) {
  if (!isSupportDispatchStatus(from) || !isSupportDispatchStatus(to)) return false;
  return SUPPORT_DISPATCH_STATUS_GRAPH[from].includes(to);
}

// The single truthfulness chokepoint for delivery language.
//
// `accepted` is the ONLY status allowed to imply an external system holds the
// message, and it is allowed only when a provider delivery id proves it. An
// `accepted` row without that id is a bookkeeping bug, and reporting it as
// accepted would be the exact lie this contract exists to prevent — so it
// degrades to an explicitly unconfirmed label instead.
export function dispatchStatusLabel(status, { providerDeliveryId = null } = {}) {
  if (!isSupportDispatchStatus(status)) return 'Delivery state unknown';
  if (status === 'accepted' && !String(providerDeliveryId || '').trim()) {
    return 'Delivery state unconfirmed';
  }
  return SUPPORT_DISPATCH_STATUS_LABELS[status];
}

export function dispatchStatusDetail(status, { providerDeliveryId = null } = {}) {
  if (!isSupportDispatchStatus(status)) {
    return 'PRIME cannot describe this delivery state. Treat the ticket as not delivered.';
  }
  if (status === 'accepted' && !String(providerDeliveryId || '').trim()) {
    return 'PRIME has no provider delivery id for this ticket, so it cannot confirm the provider accepted it.';
  }
  return SUPPORT_DISPATCH_STATUS_DETAILS[status];
}

// -------------------------------------------------------------------------
// Reporter-facing protected-information copy
// -------------------------------------------------------------------------

// One source of truth for the CUI copy, so the warning above the description,
// the warning on the review step, and the tests all read the same words.
//
// The example deliberately uses VISIBLY FICTIONAL placeholders and carries no
// realistic-looking identifier: an example that reads like real protected data
// teaches the wrong lesson and would itself trip the sensitive-content
// detectors it is meant to explain.
export const SUPPORT_TICKET_CUI_WARNING = Object.freeze({
  heading: 'Do not put protected information in this ticket',
  body: 'This ticket leaves DHA PRIME as an email to the MHS Service Desk. Do not type CUI, PHI, PII, Social Security numbers, DoD ID numbers, passwords, or access tokens into any field. Describe what PRIME did wrong — the service desk does not need the protected data to work the ticket.',
  goodExampleLabel: 'Write it like this',
  goodExample:
    'The financials export fails for one record in the FY26 sample data set (internal record ID EX-0001). The page shows "Export failed" every time.',
  badExampleLabel: 'Not like this',
  badExample:
    'The export fails for [patient name], [Social Security number], [date of birth] — with the real values typed in.',
  followUpNote:
    'If PRIME finds anything that looks like protected information, you must take it out before the ticket can be sent. There is no way to send it anyway — the box below tells the service desk that they will need protected data to work the ticket, it does not let you leave any in.',
});

export const SUPPORT_TICKET_ACKNOWLEDGEMENT =
  'I confirm that this ticket does not contain CUI, PHI, PII, credentials, access tokens, or other protected information.';
export const SUPPORT_TICKET_ACKNOWLEDGEMENT_VERSION = '2026-07-29.1';
export const SUPPORT_TICKET_CUI_ATTESTATION_SCHEMA_VERSION = 'support-cui-attestation/1.0';

// The protected-information indicator is METADATA, not a release valve.
//
// Checking it changes the rendered "Protected information required through
// approved follow-up process" line and tells the service desk to expect a
// follow-up. It has NO effect on the sensitive-content gate: every finding blocks
// preparing, sending, and exporting until the text itself is changed. The wording
// has to say that plainly, because a control a reporter believes is an override
// is worse than no control at all.
export const SUPPORT_TICKET_PROTECTED_INFO_INDICATOR =
  'The service desk will need protected information (CUI, PHI, or PII) to resolve this. Tell them to expect a follow-up — they will collect it through an approved process. This does not let you type protected information here; PRIME still blocks the ticket until any is removed.';

// The exact line the rendered email carries. Kept next to the checkbox copy so
// the two can never describe different things.
export const SUPPORT_TICKET_PROTECTED_INFO_EMAIL_LABEL =
  'Protected information required through approved follow-up process';

// -------------------------------------------------------------------------
// Pure validation helpers
// -------------------------------------------------------------------------

function fieldLength(value) {
  return value == null ? 0 : String(value).length;
}

// Structural validation only — presence, type, bounds, and category membership.
// Sensitive-content evaluation is a SEPARATE, server-authoritative pass: mixing
// them would let a client infer "field is fine" from a structural pass.
//
// Returns { valid, missing, errors } with stable field-keyed entries so a form
// can render a validation summary that links to the offending control.
export function validateSupportTicketFields(fields = {}) {
  const errors = [];
  const missing = [];

  for (const field of SUPPORT_TICKET_FIELDS) {
    const raw = fields[field];
    if (raw != null && typeof raw !== 'string') {
      errors.push({
        field,
        reason: 'type',
        message: `${SUPPORT_TICKET_FIELD_LABELS[field]} must be text.`,
      });
      continue;
    }
    const value = String(raw ?? '').trim();
    if (!value) {
      if (SUPPORT_TICKET_REQUIRED_FIELDS.includes(field)) {
        missing.push(field);
        errors.push({
          field,
          reason: 'required',
          message: `${SUPPORT_TICKET_FIELD_LABELS[field]} is required.`,
        });
      }
      continue;
    }
    const limit = SUPPORT_TICKET_FIELD_LIMITS[field];
    if (fieldLength(value) > limit) {
      errors.push({
        field,
        reason: 'length',
        message: `${SUPPORT_TICKET_FIELD_LABELS[field]} must be ${limit} characters or fewer (received ${fieldLength(value)}).`,
      });
    }
    if (field === 'category' && !isSupportTicketCategory(value)) {
      errors.push({
        field,
        reason: 'enum',
        message: 'Choose one of the listed issue or request categories.',
      });
    }
    // Structural shape only. A deliverable mailbox is proven by the trusted
    // directory value the server uses, never by a client-side pattern.
    if (field === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      errors.push({
        field,
        reason: 'format',
        message: 'Enter a work e-mail address in the form name@example.mil.',
      });
    }
  }

  if (fields.cuiAcknowledged !== true) {
    missing.push('cuiAcknowledged');
    errors.push({
      field: 'cuiAcknowledged',
      reason: 'required',
      message: 'Confirm the ticket contains no protected information before sending it.',
    });
  }

  return { valid: errors.length === 0, missing, errors };
}

// Field keys are reported the way the generic intake readiness reports them
// (`details.description`), so one validation summary can render both sources.
function readinessFieldKey(field) {
  return field === 'description' ? 'details.description' : field;
}

// Readiness projection used by the API's support-ticket readiness validator and
// mirrored by the form's submit gate.
export function supportTicketMissingFields(fields = {}) {
  const { missing } = validateSupportTicketFields(fields);
  return missing.map(readinessFieldKey);
}

/**
 * The COMPLETE structural readiness projection.
 *
 * `supportTicketMissingFields()` alone was not enough: it reports only absent
 * required fields and silently discarded the enum and format errors
 * validateSupportTicketFields() had already computed. That let an intake carrying
 * a nonempty-but-invalid e-mail address or an unlisted category prepare and submit
 * — the validator knew, and the readiness gate threw the answer away.
 *
 * Readiness requires BOTH arrays to be empty.
 */
export function evaluateSupportTicketStructure(fields = {}) {
  const { missing, errors } = validateSupportTicketFields(fields);
  const validationErrors = errors
    .filter((error) => error.reason !== 'required')
    .map((error) => ({ ...error, field: readinessFieldKey(error.field) }));
  return {
    missing: missing.map(readinessFieldKey),
    validationErrors,
    valid: missing.length === 0 && validationErrors.length === 0,
  };
}
